import { useEffect, useState } from 'react';
import {
  FaAward,
  FaClock,
  FaGlobe,
  FaHeadset,
  FaMapMarkerAlt,
  FaStar
} from 'react-icons/fa';
import { Link } from 'react-router-dom';
import Testimonials from '../components/Testimonials';
import { toursAPI } from '../services/api';
import './Home.css';

const Home = () => {
  const [featuredTours, setFeaturedTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);

  // Hero carousel slides with images and text
  const heroSlides = [
    {
      image: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1920&q=80',
      title: 'International / Domestic Flights',
      subtitle: 'Fly anywhere in the world with the best deals'
    },
    {
      image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1920&q=80',
      title: 'Luxury Accommodation',
      subtitle: 'Stay in the finest hotels and resorts worldwide'
    },
    {
      image: 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=1920&q=80',
      title: 'Customized Tour Packages',
      subtitle: 'Tailor-made trips designed just for you'
    },
    {
      image: 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=1920&q=80',
      title: 'Adventure & Exploration',
      subtitle: 'Discover breathtaking destinations around the globe'
    },
    {
      image: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1920&q=80',
      title: 'Beach Paradise Getaways',
      subtitle: 'Relax on pristine beaches with crystal clear waters'
    }
  ];

  useEffect(() => {
    fetchFeaturedTours();
  }, []);

  // Auto-slide carousel
  useEffect(() => {
    const slideInterval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(slideInterval);
  }, [heroSlides.length]);

  const fetchFeaturedTours = async () => {
    try {
      const response = await toursAPI.getAll({ featured: true, limit: 3 });
      setFeaturedTours(response.data.data);
    } catch (error) {
      console.error('Error fetching tours:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="home">
      {/* Hero Section with Carousel */}
      <section className="hero">
        {/* Image Carousel */}
        <div className="hero-carousel">
          {heroSlides.map((slide, index) => (
            <div
              key={index}
              className={`hero-slide ${index === currentSlide ? 'active' : ''}`}
              style={{ backgroundImage: `url(${slide.image})` }}
            />
          ))}
        </div>

        <div className="hero-overlay"></div>
        <div className="hero-content container">
          <h1 className="hero-title" key={currentSlide}>
            {heroSlides[currentSlide].title}
          </h1>
          <p className="hero-subtitle" key={`subtitle-${currentSlide}`}>
            {heroSlides[currentSlide].subtitle}
          </p>
          <div className="hero-buttons fade-in">
            <Link to="/tours" className="btn btn-primary">Explore Tours</Link>
            <Link to="/contact" className="btn btn-secondary">Contact Us</Link>
          </div>
        </div>

        {/* Carousel Indicators */}
        <div className="hero-indicators">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              className={`indicator ${index === currentSlide ? 'active' : ''}`}
              onClick={() => setCurrentSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        <div className="hero-scroll">
          <span>Scroll Down</span>
          <div className="scroll-arrow">↓</div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features section">
        <div className="container">
          <div className="section-title">
            <h2>Why Choose KSA Holidays?</h2>
            <p>We provide exceptional travel experiences with professional service</p>
          </div>
          <div className="features-grid grid grid-3">
            <div className="feature-card card fade-in">
              <div className="feature-icon">
                <FaGlobe />
              </div>
              <h3>Wide Selection</h3>
              <p>Choose from hundreds of carefully curated destinations worldwide</p>
            </div>
            <div className="feature-card card fade-in">
              <div className="feature-icon">
                <FaAward />
              </div>
              <h3>Best Prices</h3>
              <p>Competitive rates with no hidden fees - quality you can trust</p>
            </div>
            <div className="feature-card card fade-in">
              <div className="feature-icon">
                <FaHeadset />
              </div>
              <h3>24/7 Support</h3>
              <p>Our dedicated team is always here to assist you</p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Tours Section */}
      <section className="featured-tours section" style={{ background: 'var(--bg-light)' }}>
        <div className="container">
          <div className="section-title">
            <h2>Featured Tours</h2>
            <p>Handpicked experiences for unforgettable journeys</p>
          </div>

          {loading ? (
            <div className="spinner"></div>
          ) : featuredTours.length > 0 ? (
            <div className="tours-grid grid grid-3">
              {featuredTours.map((tour) => (
                <div key={tour._id} className="tour-card card fade-in">
                  <div className="tour-image-wrapper">
                    <img
                      src={tour.images?.[0] || 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=500'}
                      alt={tour.title}
                      className="card-image"
                    />
                    <span className="tour-badge badge badge-primary">{tour.category}</span>
                  </div>
                  <div className="card-body">
                    <h3 className="tour-title">{tour.title}</h3>
                    <div className="tour-info">
                      <span className="info-item">
                        <FaMapMarkerAlt /> {tour.destination}
                      </span>
                      <span className="info-item">
                        <FaClock /> {tour.duration}
                      </span>
                    </div>
                    <p className="tour-description">
                      {tour.description.substring(0, 100)}...
                    </p>
                    <div className="tour-footer">
                      <div className="tour-rating">
                        <FaStar className="star-icon" />
                        <span>{tour.rating || 5.0} ({tour.reviewCount || 0} reviews)</span>
                      </div>
                      <div className="tour-price">
                        <span className="price-label">Starting from</span>
                        <span className="price-value">₹{tour.price}</span>
                      </div>
                    </div>
                    <Link to={`/tours/${tour._id}`} className="btn btn-primary" style={{ width: '100%', marginTop: '15px' }}>
                      View Details
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-tours">
              <p>No featured tours available at the moment. Check back soon!</p>
            </div>
          )}

          <div className="text-center mt-3">
            <Link to="/tours" className="btn btn-secondary">View All Tours</Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Start Your Adventure?</h2>
            <p>Book your dream vacation today and create memories that last a lifetime</p>
            <Link to="/tours" className="btn btn-primary">Explore Destinations</Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <Testimonials />

      {/* Stats Section */}
      <section className="stats section" style={{ background: 'var(--bg-light)' }}>
        <div className="container">
          <div className="stats-grid">
            <div className="stat-item fade-in">
              <h3>1000+</h3>
              <p>Happy Travelers</p>
            </div>
            <div className="stat-item fade-in">
              <h3>50+</h3>
              <p>Destinations</p>
            </div>
            <div className="stat-item fade-in">
              <h3>100%</h3>
              <p>Satisfaction</p>
            </div>
            <div className="stat-item fade-in">
              <h3>24/7</h3>
              <p>Support</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
